import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  AreaCodeInfo,
  GetPhoneNumberAreaCodesParams,
  GetPhoneNumberSelectionParams,
  Line,
  SimsV2Service,
  Unsubscribe,
} from 'src/app/core';
import { CountryISO } from 'ngx-intl-tel-input';
import { phoneNumberValidator } from 'src/app/core/form-validators/phoneNumber.validator';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-end-users-update-phone-number-form',
  templateUrl: './end-users-update-phone-number-form.component.html',
  styleUrls: ['./end-users-update-phone-number-form.component.less'],
})
export class EndUsersUpdatePhoneNumberFormComponent
  extends Unsubscribe
  implements OnInit
{
  updatePhoneNumberForm: FormGroup;
  UPDATE_PHONE_NUMBER_FORM_PREFIX =
    'PAGE.MOBILE.END_USERS.UPDATE_PHONE_NUMBER.FORM.';
  updateType = {
    SELECT: 'SELECT',
    INPUT: 'INPUT',
  };
  areaCodesInfo: AreaCodeInfo[] = [];
  phoneNumbers = [];
  countryISO = CountryISO;

  @Input() line: Line;
  @Input() brandUuid: string;

  constructor(private fb: FormBuilder, private simsV2Service: SimsV2Service) {
    super();
    this.updatePhoneNumberForm = this.fb.group(
      this._updatePhoneNumberFormModel()
    );
  }

  ngOnInit(): void {
    this._getPhoneNumberAreaCodes();
  }

  private _getPhoneNumberAreaCodes() {
    let getPhoneNumberAreaCodesParams: GetPhoneNumberAreaCodesParams = {
      countryCodes: [this.countryISO.Mexico],
    };
    this.simsV2Service
      .getPhoneNumberAreaCodes(getPhoneNumberAreaCodesParams)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((getPhoneNumberAreaCodesResponse) => {
        if (getPhoneNumberAreaCodesResponse) {
          for (let areaCodeInfo of getPhoneNumberAreaCodesResponse.areaCodes) {
            if (areaCodeInfo.free) {
              this.areaCodesInfo.push(areaCodeInfo);
            }
          }
          this.areaCodesInfo.sort((a, b) => a.areaCode - b.areaCode);
        } else {
          this.areaCodesInfo = [];
        }
      });
  }

  private _updatePhoneNumberFormModel(): any {
    return {
      updateType: [null, [Validators.required]],
      areaCode: [{ value: null, disabled: true }, []],
      phoneNumberSelect: [{ value: null, disabled: true }, []],
      phoneNumberInput: [{ value: null, disabled: true }, []],
    };
  }

  onUpdateTypeChange(updateType) {
    if (updateType === this.updateType.INPUT) {
      this._enablePhoneNumberInput();
      this._disableAreaCodeSelect();
      this._disablePhoneNumberSelect();
    } else if (updateType === this.updateType.SELECT) {
      this._enableAreaCodeSelect();
      this._disablePhoneNumberInput();
    }
    this._updateFormValueAndValidity();
  }

  onUpdateAreaCode(areaCode) {
    if (areaCode) {
      this._getPhoneNumberSelection();
      this._enablePhoneNumberSelect();
    } else {
      this._disablePhoneNumberSelect();
    }
    this.updatePhoneNumberForm.get('phoneNumberSelect').setValue('');
  }

  private _getPhoneNumberSelection() {
    let getPhoneNumberSelectionParams: GetPhoneNumberSelectionParams = {
      countryCode: this.countryISO.Mexico,
      areaCodes: this.updatePhoneNumberForm.get('areaCode').value,
      amountPerAreaCode: 5,
      lineType: this.line.lineType,
      otherAreaCodes: false,
      brandId: this.brandUuid,
    };

    this.simsV2Service
      .getPhoneNumberSelection(getPhoneNumberSelectionParams)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((getPhoneNumberSelectionResponse) => {
        if (getPhoneNumberSelectionResponse.phoneNumberSelection) {
          this.phoneNumbers =
            getPhoneNumberSelectionResponse.phoneNumberSelection[
              this.updatePhoneNumberForm.get('areaCode').value
            ];
        } else {
          this.phoneNumbers = [];
        }
      });
  }

  private _disableAreaCodeSelect() {
    this.updatePhoneNumberForm.get('areaCode').disable();
    this.updatePhoneNumberForm.get('areaCode').setValue(null);
    this.updatePhoneNumberForm.get('areaCode').clearValidators();
  }

  private _disablePhoneNumberSelect() {
    this.updatePhoneNumberForm.get('phoneNumberSelect').disable();
    this.updatePhoneNumberForm.get('phoneNumberSelect').setValue(null);
  }

  private _disablePhoneNumberInput() {
    this.updatePhoneNumberForm.get('phoneNumberInput').disable();
    this.updatePhoneNumberForm.get('phoneNumberInput').setValue(null);
    this.updatePhoneNumberForm.get('phoneNumberInput').clearValidators();
  }

  private _enableAreaCodeSelect() {
    this.updatePhoneNumberForm.get('areaCode').enable();
    this.updatePhoneNumberForm
      .get('areaCode')
      .setValidators([Validators.required]);
    this.updatePhoneNumberForm.get('areaCode').markAsPristine();
    this.updatePhoneNumberForm.get('areaCode').markAsUntouched();
  }

  private _enablePhoneNumberSelect() {
    this.updatePhoneNumberForm.get('phoneNumberSelect').enable();
    this.updatePhoneNumberForm.get('phoneNumberSelect').markAsPristine();
    this.updatePhoneNumberForm.get('phoneNumberSelect').markAsUntouched();
  }

  private _enablePhoneNumberInput() {
    this.updatePhoneNumberForm.get('phoneNumberInput').enable();
    this.updatePhoneNumberForm
      .get('phoneNumberInput')
      .setValidators([Validators.required, phoneNumberValidator()]);
    this.updatePhoneNumberForm.get('phoneNumberInput').markAsPristine();
    this.updatePhoneNumberForm.get('phoneNumberInput').markAsUntouched();
  }

  private _updateFormValueAndValidity() {
    this.updatePhoneNumberForm.get('areaCode').updateValueAndValidity();
    this.updatePhoneNumberForm
      .get('phoneNumberSelect')
      .updateValueAndValidity();
    this.updatePhoneNumberForm.get('phoneNumberInput').updateValueAndValidity();
  }
}
